import { useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	AutocompleteCloseReason,
	CircularProgress,
	InputAdornment,
	Typography,
	debounce,
	useTheme
} from "@mui/material";
import { default as TextField } from "@mui/material/TextField/TextField";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { httpService } from "@sal/onevent-portal";
import { Search as SearchIcon } from "@mui/icons-material";
import { generateContainerTransportURL, generateURL } from "../utilities/consignmentHelper";

type SearchResult = {
	searchResultID: number | string;
	objectID: number | string;
	objectType: string;
	objectFunction: string;
	title: string;
	description: string;
	dataJSON?: string;
	data?: {
		value?: string;
		statusSystemName?: string;
		containerID?: number | string;
	};
	searchResultSystemName: string;
	typeCode: string;
};

export function QuickSearch() {
	const theme = useTheme();
	const queryClient = useQueryClient();

	const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

	const [, setInputValue] = useState<string>("");
	const [selectValue, setSelectValue] = useState<SearchResult | null>(null);
	const applySearchTerm = useMemo(() => debounce((value: string | undefined) => setSearchTerm(value), 500), []);

	const results = useQueries({
		queries: [
			{
				queryKey: ["QuickSearch", "PortalConsignment", searchTerm],
				queryFn: () => httpService.get("Search", { searchTerm, searchBy: "PortalConsignment" }),
				keepPreviousData: false,
				enabled: !!searchTerm
			},
			{
				queryKey: ["QuickSearch", "BLLContainerTransport", searchTerm],
				queryFn: () => httpService.get("Search", { searchTerm, searchBy: "BLLContainerTransport" }),
				keepPreviousData: false,
				enabled: !!searchTerm
			}
		]
	});

	const isLoading = results.some((result) => result.isFetching);
	const combinedData = results
		.flatMap((result) => result.data?.data ?? [])
		.map((d: SearchResult) => ({
			...d,
			data: d.dataJSON ? JSON.parse(d.dataJSON) : d.data
		}));

	useEffect(() => {
		if (searchTerm === "") {
			queryClient.resetQueries({ queryKey: ["QuickSearch"], exact: false });
		}
	}, [searchTerm]);

	const handleOnChange = (_e: React.SyntheticEvent<Element, Event>, value: SearchResult | null) => {
		if (value) {
			// for the Consignment Pages
			if (value.objectType.includes("Consignment")) {
				const url = generateURL(value.objectType, value.objectID, value.data?.statusSystemName ?? "");
				window.location.href = `/#${url}`;
				return;
			}

			const url = generateContainerTransportURL(
				value.objectType,
				value.objectID,
				value.data?.statusSystemName ?? "",
				value.title,
				value.data?.containerID
			);
			window.location.href = `/#${url}`;
		}
	};

	const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: SearchResult) => {
		return (
			<li {...props} key={`${option.searchResultID}_${option.objectID}`}>
				<Typography fontSize="0.85rem" lineHeight="0.9rem">
					{option.description}
				</Typography>
			</li>
		);
	};

	const getOptionLabel = (option: SearchResult) => {
		return option.data?.value || "";
	};

	const handleOnClose = (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
		if (reason === "selectOption") {
			setSelectValue(null);
			setInputValue("");
		}
	};

	return (
		<Autocomplete<SearchResult, false, false, false>
			value={selectValue}
			fullWidth
			clearOnBlur={false}
			loading={isLoading}
			options={combinedData}
			filterOptions={(options) => options}
			onChange={handleOnChange}
			onClose={handleOnClose}
			onInputChange={(event, value, reason) => {
				const term = reason === "clear" ? undefined : value;
				setInputValue(value);
				applySearchTerm(term);
			}}
			getOptionLabel={getOptionLabel}
			renderOption={renderOption}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder={"Quick search"}
					InputProps={{
						...params.InputProps,
						endAdornment: <>{isLoading && <CircularProgress color="inherit" size="1rem" />}</>,
						startAdornment: (
							<InputAdornment position="end" sx={{ color: theme.palette.primary.main }}>
								<SearchIcon />
							</InputAdornment>
						)
					}}
				/>
			)}
			sx={{
				".MuiAutocomplete-input": {
					padding: "4px 8px !important", // Align with the UserOrganisationToggle
					fontSize: "0.85rem"
				}
			}}
		/>
	);
}
